/**
 * Server state for people. TanStack Query owns everything the server knows;
 * nothing here is ever copied into Zustand.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OrgRole } from "@/features/session/types";
import { peopleApi, peopleKeys, type PersonCreate } from "./api";

export function usePeople() {
  return useQuery({ queryKey: peopleKeys.list(), queryFn: peopleApi.list });
}

function useInvalidatePeople() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: peopleKeys.all });
}

export function useAddPerson() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: (payload: PersonCreate) => peopleApi.add(payload),
    onSuccess: invalidate,
  });
}

export function useSetOrgRole() {
  const invalidate = useInvalidatePeople();
  return useMutation({
    mutationFn: ({ sub, role }: { sub: string; role: OrgRole }) => peopleApi.setOrgRole(sub, role),
    onSuccess: invalidate,
  });
}

export function useOffboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sub: string) => peopleApi.offboard(sub),
    // Offboarding also removes them from every workspace.
    onSuccess: () => queryClient.invalidateQueries(),
  });
}
